import { ErrorToastOptions } from '../interfaces/wish-list-error-toast.interface';
import { ErrorToastWishList } from '../constants/wish-list-error-toast-status';
import { ProductCardData } from '../../product-card/product-card-display';
import { WishList } from '../interfaces/wish-list-items.interface';
import { WishListProductAction } from '../interfaces/wish-list-update-product.interface';
import { html, nothing, TemplateResult } from 'lit';
import {
  _renderLoadingTemplate,
  getGenericErrorMessage,
  getLimitErrorMessage,
  getWishListItemsURL,
} from './wish-list.util';

const ERROR_TOAST_MAP = {
  [ErrorToastWishList.ADDING_PRODUCT_TO_WISH_LIST]: {
    header: 'Error Adding Product to Wish List',
    body: getGenericErrorMessage(),
  },
  [ErrorToastWishList.REMOVING_PRODUCT_FROM_WISH_LIST]: {
    header: 'Error Removing Product from Wish List',
    body: getGenericErrorMessage(),
  },
  [ErrorToastWishList.FETCHING_WISH_LISTS]: {
    header: 'Error Fetching Wishlists',
    body: getGenericErrorMessage(),
  },
  [ErrorToastWishList.FETCHING_WISH_LIST_ITEMS]: {
    header: 'Error Fetching Wishlist Items',
    body: getGenericErrorMessage(),
  },
  [ErrorToastWishList.CREATING_WISH_LIST]: {
    header: 'Error Creating Wish List',
    body: getGenericErrorMessage(),
  },
  [ErrorToastWishList.CREATING_WISH_LIST_LIMIT_EXCEEDED]: {
    header: 'Error Creating Wish List Limit Exceeded',
    body: getLimitErrorMessage(),
  },
  [ErrorToastWishList.DELETING_WISH_LIST]: {
    header: 'Error Deleting Wish List',
    body: getGenericErrorMessage(),
  },
  [ErrorToastWishList.UPDATING_WISH_LIST]: {
    header: 'Error Updating Wish List',
    body: getGenericErrorMessage(),
  },
  [ErrorToastWishList.UPDATING_WISH_LIST_COMMENT]: {
    header: 'Error Updating Product Wish List Comment',
    body: getGenericErrorMessage(),
  },
  [ErrorToastWishList.BULK_ADD_TO_CART] : {
    header: 'Error Adding Products To Cart',
    body: getGenericErrorMessage(),
  }
};

/**
 * Returns the global toast container element.
 *
 * To be used for the toast to be visible when closing off-canvas.
 */
export function getGlobalToastContainer(): HTMLElement | undefined {
  return document.getElementsByClassName('uxt-mhcorp')[0] as HTMLElement;
}

/**
 * Renders the error toast.
 *
 * @param options
 */
export function renderErrorToast(options: ErrorToastOptions) {
  return html`
    <mhecomm-toast
      type="primary"
      header=${ERROR_TOAST_MAP[options?.error].header}
      .body=${ERROR_TOAST_MAP[options?.error].body}
      ?autoHide=${options?.autoHide}
      ?hideHeader=${options?.hideHeader}
      ?hideClose=${options?.hideClose}
      ?showOnlyBody=${options?.showOnlyBody}
    ></mhecomm-toast>
    ${options?.displayLoadingTemplate ? _renderLoadingTemplate() : nothing}
  `;
}

/**
 * Renders the success toast when updating a product on a wish list.
 *
 * @param action
 * @param product
 * @param wishList
 */
export function renderUpdateProductSuccessToast(
  action: WishListProductAction,
  product?: ProductCardData,
  wishList?: WishList
): TemplateResult {
  if (!product || !wishList) {
    return html``;
  }

  return html`
    <mhecomm-toast
      .body=${getProductUpdateSuccessMessage(action, product, wishList)}
      type="light"
      headerIcon="folder-plus"
      ?hideClose="${true}"
      ?autoHide="${false}"
      ?showOnlyBody="${true}"
    ></mhecomm-toast>
  `;
}

/**
 * Renders the success toast when updating a product comment on a wish list.
 */
export function renderCommentUpdateSuccess(): TemplateResult {
  return html`
    <mhecomm-toast
      .body=${html`The comment has been successfully updated.`}
      type="light"
      ?hideClose="${true}"
      ?autoHide="${false}"
      ?showOnlyBody="${true}"
    ></mhecomm-toast>
  `;
}

/**
 * Renders the success toast when creating a wish list.
 */
export function renderCreateWishListSuccessToast(wishList?: WishList): TemplateResult {
  return html`
    <mhecomm-toast
      .body=${getCreateWishListSuccessMessage(wishList)}
      type="light"
      headerIcon="folder-plus"
      ?hideClose="${true}"
      ?hideHeader="${true}"
      ?autoHide="${false}"
      ?showOnlyBody="${true}"
    ></mhecomm-toast>
  `;
}

/**
 * Renders the success toast when deleting a wish list.
 */
export function renderDeleteWishListSuccessToast(wishListName?: string): TemplateResult {
  return html`
    <mhecomm-toast
      .body=${getDeleteWishListSuccessMessage(wishListName)}
      type="light"
      headerIcon="trash"
      ?hideClose="${true}"
      ?hideHeader="${true}"
      ?autoHide="${false}"
      ?showOnlyBody="${true}"
    ></mhecomm-toast>
  `;
}

/**
 * Renders the success toast when editing a wish list.
 */
export function renderEditWishListSuccessToast(wishList?: WishList): TemplateResult {
  return html`
    <mhecomm-toast
      .body=${getEditWishListSuccessMessage(wishList)}
      type="light"
      headerIcon="folder-plus"
      ?hideClose="${true}"
      ?hideHeader="${true}"
      ?autoHide="${false}"
      ?showOnlyBody="${true}"
    ></mhecomm-toast>
  `;
}

/**
 * Renders the success toast for add all items to list.
 */
export function renderAddAllItemsToCartSuccessToast(productsCount?: number): TemplateResult {
  return html`
    <mhecomm-toast
      .body=${getAddAllItemsToCartSuccessMessage(productsCount)}
      type="light"
      headerIcon="trash"
      ?hideClose="${true}"
      ?hideHeader="${true}"
      ?autoHide="${false}"
      ?showOnlyBody="${true}"
    ></mhecomm-toast>
  `;
}

/**
 * Returns the success message when updating a product in a wish list.
 */
function getProductUpdateSuccessMessage(
  action: WishListProductAction,
  product: ProductCardData,
  wishList: WishList
): TemplateResult {
  const actionText: string = action === WishListProductAction.ADD ? 'added to' : 'removed from';

  return html`
    <span>
      ${product?.url && product?.title ? html`<a href="${product.url}">${product.title}</a>` : `The product`} has been
      ${actionText} your list <a href="${getWishListItemsURL(wishList.wishlist_id)}">${wishList?.name}</a>.
    </span>
  `;
}

/**
 * Returns the success message when creating a wish list.
 */
function getCreateWishListSuccessMessage(wishList?: WishList): TemplateResult {
  return html`
    <span>
      Your list ${wishList ? html`<a href="${getWishListItemsURL(wishList.wishlist_id)}">${wishList?.name}</a>` : ''}
      has been created.
    </span>
  `;
}

/**
 * Returns the success message when deleting a wish list.
 */
function getDeleteWishListSuccessMessage(wishListName?: string): TemplateResult {
  return html`
    <span>
      Your list ${wishListName ? html`<span class="fw-bold">${wishListName}</span>` : ''} has been deleted.
    </span>
  `;
}

/**
 * Returns the success message when editing a wish list.
 */
function getEditWishListSuccessMessage(wishList?: WishList): TemplateResult {
  return html`
    <span>
      Your list ${wishList ? html`<a href="${getWishListItemsURL(wishList.wishlist_id)}">${wishList?.name}</a>` : ''}
      has been updated.
    </span>
  `;
}

/**
 * Returns the success message when all items to cart.
 */
function getAddAllItemsToCartSuccessMessage(productsCount?: number): TemplateResult {
  return html`
    <span>
      ${productsCount} items have been added to cart.
    </span>
  `;
}
